import { useEffect, useMemo, useRef, type JSX } from 'react';

import Head from 'next/head';
import dynamic from 'next/dynamic';

import { fontClassNames } from 'font';

import { templates } from 'templates';

import RouteProgress from 'libs/router/RouteProgress';
import { keys } from 'libs/object/object';
import { clearTimeoutIfExists, patchReactIssue11538 } from 'libs/node';
import { withDomainSuffix } from 'libs/seo';

import { Alternative } from 'stores/intl/ints.types';

import { UserCountryProvider } from 'context/UserCountryContext';
import { LeadProvider } from 'context/LeadContext';
import { ConsentProvider } from 'context/ConsentContext';
import { ContentProvider, initialValues as initialContentValues } from 'context/ContentContext';

import { withErrorHandling } from 'hocs/withErrorHandling';

import { useIsIframe } from 'hooks/useIsIframe';
import { useEventListener } from 'hooks/useEventListener';

// import { ExtensionModal } from 'ui/organisms/ExtensionModal/ExtensionModal';
import { MetadataWidget } from 'ui/atoms/MetadataWidget/MetadataWidget';
import { OverleadWidget } from 'ui/atoms/OverleadWidget';
import { DynamicImport } from 'ui/DynamicImport';

import Header from 'features/layout/organisms/Header/Header';
import { FooterLoader } from 'features/layout/organisms/Footer/FooterLoader/FooterLoader';
import { Layout, LayoutContainer, LayoutContent } from 'features/layout/organisms/Layout';

patchReactIssue11538();

const alignURLPattern = (item: Alternative) => ({
  ...item,
  link: item.link === '' ? '/' : item.link,
});

type Props = {
  statusCode: number;
};

export const Page = ({ statusCode, ...props }: Props & typeof initialContentValues): JSX.Element => {
  const { isIframe } = useIsIframe();
  const scrollThrottle = useRef<NodeJS.Timeout>(undefined);

  useEffect(() => {
    return () => {
      clearTimeoutIfExists(scrollThrottle.current);
    };
  }, []);

  useEventListener('scroll', () => {
    if (scrollThrottle.current) {
      return;
    }

    scrollThrottle.current = setTimeout(() => {
      window.requestAnimationFrame(() => {
        scrollThrottle.current = undefined;

        if (!window) {
          return;
        }

        const header = document.getElementById('header');
        if (header) {
          if (['payg', 'payg_second'].includes(props.data.page_type)) {
            header.style.position = 'absolute';

            return;
          }
        }
      });
    }, 500);
  });

  const alternates = useMemo(() => {
    if (Array.isArray(props.links) === false) {
      return [];
    }

    const links = props.links.map(alignURLPattern);

    if (links.length === 0 || Number(props.page) !== 1) {
      return [
        alignURLPattern({
          key: props.lang,
          link: props.pathname,
        }),
      ];
    }

    if (links.find((i) => i.link === props.pathname) === undefined) {
      /**
       * For rewritten routes in next.config.js files
       * TBD: Potencially can be hardcoded so we get alternates even for this case
       * */
      return [];
    }

    return links;
  }, [props.page, props.links, props.pathname]);

  const renderTemplate = useMemo(() => {
    if (props.data.page_type in templates) {
      const Template = templates[props.data.page_type as keyof typeof templates];
      return <Template />;
    }
  }, [props.data.page_type]);

  const metadataEntries = useMemo(() => {
    if (process.env.NODE_ENV !== 'development') {
      return [];
    }

    return [
      { label: 'page', data: props.data },
      { label: 'additionals', data: props.additionals },
      {
        label: 'navigation',
        data: {
          navigation: props.navigation,
          clientNavigationGroups: props.clientNavigationGroups,
          navigationGroups: props.navigationGroups,
          secondaryNavigation: props.secondaryNavigation,
        },
      },
      { label: 'pageVars', data: props.pageVars },
      { label: 'internationalization', data: props.internationalization },
      { label: 'config', data: props.config },
    ];
  }, [
    props.data,
    props.additionals,
    props.navigation,
    props.clientNavigationGroups,
    props.navigationGroups,
    props.secondaryNavigation,
    props.pageVars,
    props.internationalization,
    props.config,
  ]);

  return (
    <>
      <RouteProgress />

      {process.env.TRACKING_DISABLED !== 'true' && process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID && <OverleadWidget />}

      {process.env.NODE_ENV === 'development' && <MetadataWidget entries={metadataEntries} />}

      <main data-page-type="ssr" className={fontClassNames}>
        <Head>
          <meta name="viewport" content="width=device-width" />

          {alternates.map((i) => (
            <link key={i.key} rel="alternate" hrefLang={i.key} href={`${process.env.HOST || ''}${i.link || ''}`} />
          ))}

          {props.data.not_indexed ? <meta name="robots" content="noindex, nofollow" /> : null}

          {props.data.document_title || props.data.title ? (
            <>
              <meta
                key="og:title"
                property="og:title"
                content={withDomainSuffix(props.data.document_title || props.data.title)}
              />
              <title key="title">{withDomainSuffix(props.data.document_title || props.data.title)}</title>
            </>
          ) : null}

          {props.data.meta_description && (
            <>
              <meta key="description" name="description" content={withDomainSuffix(props.data.meta_description)} />
              <meta
                key="og:description"
                property="og:description"
                content={withDomainSuffix(props.data.meta_description)}
              />
            </>
          )}

          {props.data.meta_background?.url && <meta property="og:image" content={props.data.meta_background.url} />}
        </Head>

        <UserCountryProvider>
          <LeadProvider>
            <ConsentProvider>
              <ContentProvider
                {...keys(initialContentValues).reduce(
                  (acc, current) => ({ ...acc, [current]: props[current] }),
                  {} as typeof initialContentValues,
                )}
              >
                <Layout designVariant={props.data.design_v2 ? 2 : 1} className={props.data.page_type}>
                  <LayoutContainer>
                    {!isIframe && <Header />}

                    <LayoutContent>{renderTemplate}</LayoutContent>
                  </LayoutContainer>

                  {!isIframe && (
                    <DynamicImport fallback={() => <FooterLoader />}>
                      {() => {
                        const Component = dynamic(() => import('features/layout/organisms/Footer/Footer'), {
                          ssr: false,
                          loading: () => <FooterLoader />,
                        });

                        return <Component hasBookDemo={props.data.has_footer_cta !== false} />;
                      }}
                    </DynamicImport>
                  )}
                </Layout>
              </ContentProvider>
            </ConsentProvider>
          </LeadProvider>
        </UserCountryProvider>
      </main>
    </>
  );
};

export const PageWithErrorHandling = withErrorHandling(Page);
